import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import ConfirmContext from 'contexts/ConfirmContext';
import {
  change,
  destroy,
  initialize,
  submit,
  updateSyncErrors
} from 'redux-form';

import {
  AppHeader,
  CircularLoader,
  RadioBtnTabsMenu,
  TabsMenu,
  TabsPanel,
  AccommodationGeneralCreate,
  AccommodationGeneralCreateSecondLanguage,
  AccommodationDescriptionCreate,
  AddOns,
  RelatedActivities,
  Integration,
  AccommodationPricesCreate,
  FeeCategorySection,
  MandatoryFees
} from 'components';

import {
  CREATE_ACCOMMODATION,
  UPDATE_ACCOMMODATION
} from 'graphql/mutations/accommodation';

import { isAccommodationFormDone } from 'utils/accommodations-and-activities/accommodationFormDoneHandler';
import { onGeneralAndDescFormsHandler } from 'utils/accommodations-and-activities/generalAndDescriptionFormsHandler';
import { getPreparedDataToCreateAccommodation } from 'utils/accommodations-and-activities/prepareAccommodationData';
import {
  isNextStepAvailable,
  isCurrentStepAvailable
} from 'utils/accommodations-and-activities/accommodationNextStepAvailableHandler';
import {
  isDiscardSection,
  discardSection
} from 'utils/accommodations-and-activities/accommodationDiscardChangesHandler';
import {
  discardChanges,
  isDiscardChanges
} from 'utils/accommodations-and-activities/activityDiscardChangesHandler';

import { DEFAULT_LANGUAGE } from 'pages/create-activity/constants';

import { clearDeletedFields, deletedMandatoryFees } from 'actions/activityAndAccommodation';

import {
  INITIAL_ACCOMMODATION_FORM_DATA,
  ACCOMMODATION_FORMS_NUMBERS,
  ACCOMMODATION_FORMS_ORDER,
  ACCOMMODATIONS_TAB
} from 'pages/create-accommodation/constants';

import {
  addAddOnsLanguage,
  deleteAddOnsLanguage
} from 'utils/accommodations-and-activities/addOnsLanguagesHandler';
import { getFileByUrl } from '../../utils/files/handleFIles';

import { useStyles } from './styles';
import { CANCEL_ACCOMMODATION_BOOKING } from '../../graphql/mutations/booking';
import { addMandatoryFeesLanguage,deleteMandatoryFeesLanguage } from 'utils/accommodations-and-activities/mandatoryFeeLanguageHandler';


const DEFAULT_COLOR = '#0E8BB4';
const CreateAccommodation = ({
  data,
  pricesActiveSubTabNumber,
  loading,
  isCopy,
  bookDates,
  ...props
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [createAccommodation] = useMutation(CREATE_ACCOMMODATION);
  const [updateAccommodation] = useMutation(UPDATE_ACCOMMODATION);
  const [cancelAccommodationBooking] = useMutation(
    CANCEL_ACCOMMODATION_BOOKING
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [isVisitedPrices] = React.useState(false);
  const [isSaveUnsavedChanges, setIsSaveUnsavedChanges] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState(0);
  const [scheduleActiveSubTab] = React.useState(0);
  const [pricesActiveSubTab, setPricesActiveSubTab] = React.useState(0);

  const [selectedLanguage, setSelectedLanguage] = React.useState(
    DEFAULT_LANGUAGE
  );
  const [languages, setLanguages] = React.useState([DEFAULT_LANGUAGE]);

  const [initialFormData, setInitialFormData] = React.useState({
    ...INITIAL_ACCOMMODATION_FORM_DATA
  });
  const [savedForms, setSavedForms] = React.useState({});

  const [color, setColor] = React.useState(DEFAULT_COLOR);
  const [savedColor, setSavedColor] = React.useState({});

  const {
    initializeForm,
    destroyForm,
    submitForm,
    deletedAddOns,
    deletedMandatoryFees,
    changeFormFieldValue,
    updateFormErrors,
    deletedRelatedActivities,
    deletedIntegrations,
    clearDeletedFields
  } = props;

  React.useEffect(() => {
    destroySecondLanguageForms();
    clearDeletedFields();
    setLanguages([DEFAULT_LANGUAGE]);
    setInitialFormData({ ...INITIAL_ACCOMMODATION_FORM_DATA });

    if (loading) {
      setIsLoading(true);
      return;
    }

    if (data) {
      setIsUpdate(true);
      setSavedForms({
        ...data,
        pricesTab:
          ACCOMMODATION_FORMS_ORDER[ACCOMMODATION_FORMS_NUMBERS.pricesForm][
            pricesActiveSubTabNumber
          ]
      });
      setPricesActiveSubTab(pricesActiveSubTabNumber);
    }

    let newInitialFormData = {
      ...INITIAL_ACCOMMODATION_FORM_DATA,
      ...data
    };

    setInitialFormData(newInitialFormData);
    initializeForms(newInitialFormData);
    setIsLoading(false);
  }, [loading]);

  const newInitialFormData = React.useMemo(() => ({
    ...INITIAL_ACCOMMODATION_FORM_DATA,
    ...data,
  }), [data]);

  React.useEffect(() => {
    if (newInitialFormData?.generalForm?.color) {
      setColor(newInitialFormData.generalForm.color);
    }
  }, [newInitialFormData?.generalForm?.color]);

  const initializeForms = initialFormsData => {
    if (
      initialFormsData['generalForm-Norwegian'] &&
      initialFormsData['generalForm-Norwegian'].title
    ) {
      setLanguages([...languages, 'Norwegian']);
    }

    Object.keys(initialFormsData).map(field => {
      updateFormErrors(field, {});
      initializeForm(field, initialFormsData[field]);
    });
  };

  const destroySecondLanguageForms = () => {
    const forms = ['generalForm-Norwegian', 'descriptionForm-Norwegian'];
    destroyForm(forms);
  };

  const replacePhotosToFiles = async data => {
    if (!!data.addOns) {
      for (const addOn of data.addOns) {
        if (!addOn.photo || typeof addOn.photo !== 'string') continue;
        addOn.photo = await getFileByUrl(addOn.photo);
      }
    }

    if (!data.photo) {
      data.photo = await getFileByUrl(
        props['generalForm'].values.coverPhoto.image
      );
    }

    return data;
  };

  const onCreateActivityHandler = async () => {
    const activePricesForm =
      props[
        ACCOMMODATION_FORMS_ORDER[ACCOMMODATION_FORMS_NUMBERS.pricesForm][
          pricesActiveSubTab
        ]
      ];

    if (activePricesForm.syncErrors) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can save'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      return;
    }

    setIsLoading(true);

    let data = getPreparedDataToCreateAccommodation(
      props,
      pricesActiveSubTab,
      languages,
      isUpdate,
      deletedAddOns,
      deletedMandatoryFees,
      deletedRelatedActivities,
      deletedIntegrations,
      isCopy
    );

    if (isUpdate && !isCopy) {
      const removeOldDeactivationBookings = data.deactivationPeriods.map(
        ({ id }) =>
          id?.includes('new-one')
            ? Promise.resolve()
            : cancelAccommodationBooking({ variables: { id } })
      );

      await Promise.all(removeOldDeactivationBookings);

      updateAccommodation({
        variables: {
          ...data,
          deactivationPeriods: data.deactivationPeriods
            .filter(({ isCanceled }) => !isCanceled)
            .map(({ id, ...date }) => date)
        }
      }).then(
        () => {
          enqueueSnackbar(t('Accommodation has been successfully updated'), {
            variant: 'success'
          });
          history.push({
            pathname: '/accommodations-and-activities',
            state: { tabNumber: ACCOMMODATIONS_TAB }
          });
        },
        () => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
          setIsLoading(false);
        }
      );
      return;
    }

    if (isCopy) data = await replacePhotosToFiles(data);

    createAccommodation({
      variables: {
        ...data,

        deactivationPeriods: data.deactivationPeriods
          .filter(({ isCanceled }) => !isCanceled)
          .map(({ id, ...date }) => date)
      }
    }).then(
      () => {
        enqueueSnackbar(
          t(
            `Accommodation has been successfully ${
              isCopy ? 'copied' : 'created'
            }`
          ),
          {
            variant: 'success'
          }
        );
        history.push({
          pathname: '/accommodations-and-activities',
          state: { tabNumber: ACCOMMODATIONS_TAB }
        });
      },
      () => {
        enqueueSnackbar(t('Something was wrong'), {
          variant: 'error'
        });
        setIsLoading(false);
      }
    );
  };

  const deleteLanguage = deletedItem => {
    confirm({
      title: 'Are you sure you want to delete this tab?',
      description: t('All related tabs in this language will also be deleted.'),
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        setIsSaveUnsavedChanges(true);

        const languagesCopy = [...languages];
        const updatedLanguages = languagesCopy.filter(
          item => item !== deletedItem
        );
        setSelectedLanguage(DEFAULT_LANGUAGE);
        setLanguages(updatedLanguages);

        deleteAddOnsLanguage(
          props,
          deletedItem,
          changeFormFieldValue,
          activeTab,
          setActiveTab,
          setSavedForms,
          savedForms,
          updateFormErrors,
          submitForm
        );
        deleteMandatoryFeesLanguage(
          props,
          deletedItem,
          changeFormFieldValue,
          activeTab,
          setActiveTab,
          setSavedForms,
          savedForms,
          updateFormErrors,
          submitForm
        );
        deleteFormLanguage(deletedItem);

        setIsSaveUnsavedChanges(false);
      })
      .catch(() => {});
  };

  const deleteFormLanguage = deletedLanguage => {
    const general = `generalForm-${deletedLanguage}`;
    const description = `descriptionForm-${deletedLanguage}`;

    destroyForm([general, description]);
    setSavedForms({
      ...savedForms,
      [general]: {},
      [description]: {}
    });
  };

  const onActiveTabChangeHandler = (event, newActiveTab) => {
    if (
      !isNextStepAvailable(
        props,
        languages,
        newActiveTab,
        savedForms,
        pricesActiveSubTab
      )
    ) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can go to this step'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      return false;
    }

    let formName = ACCOMMODATION_FORMS_ORDER[activeTab];

    if (activeTab === ACCOMMODATION_FORMS_NUMBERS.pricesForm) {
      formName = formName[pricesActiveSubTab];
    }

    if (
      isDiscardSection(activeTab, newActiveTab, pricesActiveSubTab, savedForms)
    ) {
      onDiscardChangesHandler(formName, newActiveTab, true);
      return;
    }

    if (
      isDiscardChanges(
        props,
        formName,
        activeTab,
        newActiveTab,
        savedForms,
        initialFormData,
        {},
        languages
      )
    ) {
      onDiscardChangesHandler(formName, newActiveTab, false);
      return;
    }

    setActiveTab(newActiveTab);
  };

  const onDiscardChangesHandler = (
    formName,
    newActiveTab,
    isDiscardSection
  ) => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to discard the changes?'),
      confirmationText: 'Save',
      cancellationText: 'Discard'
    })
      .then(() => {
        setIsSaveUnsavedChanges(true);
        submitForm(formName);

        if (!isCurrentStepAvailable(props, activeTab, pricesActiveSubTab)) {
          setIsSaveUnsavedChanges(false);
          return;
        }

        if (
          isNextStepAvailable(
            props,
            languages,
            newActiveTab,
            savedForms,
            pricesActiveSubTab
          )
        ) {
          setActiveTab(newActiveTab);
        } else {
          enqueueSnackbar(
            t(
              'You should complete and save all previous steps before you can go to this step'
            ),
            {
              variant: 'info',
              autoHideDuration: 5000
            }
          );
        }

        setIsSaveUnsavedChanges(false);
      })
      .catch(() => {
        if (isDiscardSection) {
          discardSection(
            activeTab,
            newActiveTab,
            setPricesActiveSubTab,
            setActiveTab,
            initializeForm,
            savedForms,
            initialFormData
          );
          return;
        }
        discardChanges(
          languages,
          formName,
          initializeForm,
          savedForms,
          {},
          initialFormData,
          newActiveTab,
          activeTab,
          setActiveTab
        );
      });
  };

  const onChangeActivityTypeHandler = savedFormsCopy => {
    const pricesTabIndex =
      props['generalForm'].values.activityType === 'Open' ? 1 : 0;
    setPricesActiveSubTab(0);
    setSavedForms({
      ...savedFormsCopy,
      pricesTab:
        ACCOMMODATION_FORMS_ORDER[ACCOMMODATION_FORMS_NUMBERS.pricesForm][
          pricesTabIndex
        ]
    });
  };

  const confirmActivityType = (savedFormsCopy, secondFormName) => {
    confirm({
      title: 'Are you sure you want to change the activity type?',
      description:
        'Note, that previously filled in data on the Prices may be reset.',
      confirmationText: 'Change',
      cancellationText: 'Back'
    })
      .then(() => {
        onChangeActivityTypeHandler(savedFormsCopy);
        setActiveTab(ACCOMMODATION_FORMS_NUMBERS[secondFormName]);
      })
      .catch(() => {});
  };

  const showSnackbarToFillAllField = () => {
    enqueueSnackbar(
      t(
        'Please fill in all the required fields correctly on the current step for all languages'
      ),
      {
        variant: 'warning'
      }
    );
  };

  const [submitButton, setSubmitButton] = React.useState('');

  const onGeneralAndDescFormSubmitHandler = (
    languagesLength,
    secondLanguage,
    currentFormName,
    secondFormName
  ) => {
    const savedFormsCopy = onGeneralAndDescFormsHandler(
      props,
      initialFormData,
      savedForms,
      languagesLength,
      secondLanguage,
      secondFormName,
      currentFormName,
      isVisitedPrices,
      isSaveUnsavedChanges,
      onChangeActivityTypeHandler,
      showSnackbarToFillAllField,
      confirmActivityType
    );

    if (!savedFormsCopy) {
      return;
    }

    setSavedForms({
      ...savedFormsCopy
    });

    if (submitButton === 'save' && !isSaveUnsavedChanges) {
      onCreateActivityHandler();
    }
    if (submitButton === 'next' && !isSaveUnsavedChanges) {
      setActiveTab(ACCOMMODATION_FORMS_NUMBERS[secondFormName]);
    }
  };

  const getPropsForTabsWithLanguages = () => {
    return {
      selectedLanguage: selectedLanguage,
      languages: languages,
      addLanguage: newItem => {
        setLanguages([...languages, newItem]);
        addAddOnsLanguage(
          props,
          newItem,
          changeFormFieldValue,
          updateFormErrors,
          setSavedForms,
          savedForms
        );
      },
      deleteLanguage: deleteLanguage,
      selectLanguage: item => setSelectedLanguage(item)
    };
  };

  const getMandatoryFeesPropsForTabsWithLanguages = () => {
    return {
      selectedLanguage: selectedLanguage,
      languages: languages,
      addLanguage: newItem => {
        setLanguages([...languages, newItem]);
        addMandatoryFeesLanguage(
          props,
          newItem,
          changeFormFieldValue,
          updateFormErrors,
          setSavedForms,
          savedForms
        );
      },
      deleteLanguage: deleteLanguage,
      selectLanguage: item => setSelectedLanguage(item)
    };
  };

  const getGeneralCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const generalFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'generalForm',
          'descriptionForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <AccommodationGeneralCreate
            {...generalFormProps}
            initialValues={INITIAL_ACCOMMODATION_FORM_DATA.generalForm}
            isUpdate={isUpdate}
            color={color}
            colorHandler={setColor}
            setSubmitButton={setSubmitButton}
            bookDates={bookDates}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
            <AccommodationGeneralCreateSecondLanguage
              {...generalFormProps}
              form={`generalForm-${secondLanguage}`}
              isUpdate={isUpdate}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };

  const getDescriptionCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const descriptionFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'descriptionForm',
          'pricesForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <AccommodationDescriptionCreate
            {...descriptionFormProps}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
            <AccommodationDescriptionCreate
              {...descriptionFormProps}
              isUpdate={isUpdate}
              form={`descriptionForm-${secondLanguage}`}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    {
      name: 'General',
      formName: 'generalForm',
      component: getGeneralCreateTab()
    },
    {
      name: 'Description',
      formName: 'descriptionForm',
      component: getDescriptionCreateTab()
    },
    {
      name: 'Prices',
      formName: 'pricesForm',
      component: (
        <AccommodationPricesCreate
          onSubmit={(values, formName) => {
            setSavedForms({
              ...savedForms,
              [formName]: { ...values },
              pricesTab: formName
            });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACCOMMODATION_FORMS_NUMBERS.addOnsForm);
            }
          }}
          selectedTabIndex={pricesActiveSubTab}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      ),
      subTabs: [
        {
          title: 'Fixed prices',
          instruction: 'One price for whole accommodation per day'
        },
        {
          title: 'Price per person',
          instruction: 'Prices for multiple categories'
        },
        {
          title: 'Daily prices',
          instruction: 'Prices per each day'
        }
      ]
    },
    {
      name: 'Add-ons',
      formName: 'addOnsForm',
      component: (
        <AddOns
          {...getPropsForTabsWithLanguages()}
          onSubmit={values => {
            setSavedForms({ ...savedForms, addOnsForm: { ...values } });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACCOMMODATION_FORMS_NUMBERS.mandatoryFeesForm);
            }
          }}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      )
    },
    {
      name: 'Mandatory Fee',
      formName: 'mandatoryFeesForm',
      component:(
        <MandatoryFees
        {...getMandatoryFeesPropsForTabsWithLanguages()}
        onSubmit={values => {
          setSavedForms({ ...savedForms, mandatoryFeesForm: { ...values } });
          if (submitButton === 'save' && !isSaveUnsavedChanges) {
            onCreateActivityHandler();
          }
          if (submitButton === 'next' && !isSaveUnsavedChanges) {
            setActiveTab(ACCOMMODATION_FORMS_NUMBERS.relatedActivitiesForm);
          }
        }}
        setSubmitButton={setSubmitButton}
        isUpdate={isUpdate}
      />
      ),
      
    },
    {
      name: 'Related Activities',
      formName: 'relatedActivitiesForm',
      component: (
        <RelatedActivities
          onSubmit={values => {
            setSavedForms({
              ...savedForms,
              relatedActivitiesForm: { ...values }
            });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACCOMMODATION_FORMS_NUMBERS.integrationForm);
            }
          }}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      )
    },
    {
      name: 'Integration',
      formName: 'integrationForm',
      component: (
        <Integration
          isAccommodation={true}
          onSubmit={values => {
            setSavedForms({
              ...savedForms,
              integrationForm: { ...values }
            });
            if (submitButton === 'next' && !isSaveUnsavedChanges)
              onCreateActivityHandler();
          }}
          setSubmitButton={setSubmitButton}
        />
      )
    }
  ];

  return (
    <>
      <AppHeader />
      <CircularLoader
        isLoading={isLoading}
        loaderClassName={classes.loaderClassName}
      />
      <div className={classes.root}>
        <TabsMenu
          activeTab={activeTab}
          title={
            isUpdate && !isCopy
              ? 'Edit Accommodation'
              : isCopy
              ? 'Copy Accommodation'
              : 'Create new Accommodation'
          }
          isDoneTab={tab => {
            return isAccommodationFormDone(
              tab.formName,
              languages,
              props,
              savedForms,
              activeTab,
              scheduleActiveSubTab,
              pricesActiveSubTab
            );
          }}
          tabs={tabs}
          change={onActiveTabChangeHandler}
        />

        <RadioBtnTabsMenu
          activeTab={activeTab}
          activeSubTab={pricesActiveSubTab}
          tabs={tabs}
          change={e => setPricesActiveSubTab(parseInt(e.target.value))}
        />

        <div className={classes.content}>
          <div className={classes.contentBox}>
            <TabsPanel tabs={tabs} activeTab={activeTab} />
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => {
  return {
    ...state.form,
    businessOwner: state.businessOwner,
    deletedAddOns: state.activityAndAccommodation.deletedAddOns,
    deletedMandatoryFees: state.activityAndAccommodation.deletedMandatoryFees,
    deletedRelatedActivities:
      state.activityAndAccommodation.deletedRelatedActivities,
    deletedIntegrations: state.activityAndAccommodation.deletedIntegrations
  };
};

const mapDispatchToProps = dispatch => ({
  destroyForm: forms => {
    dispatch(destroy(...forms));
  },
  submitForm: form => {
    dispatch(submit(form));
  },
  changeFormFieldValue: (form, field, value) => {
    dispatch(change(form, field, value));
  },
  updateFormErrors: (form, fields) => {
    dispatch(updateSyncErrors(form, fields));
  },
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  clearDeletedFields: () => {
    dispatch(clearDeletedFields());
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateAccommodation);
